// --- External Dependencies
import React, {useCallback, useMemo, useState} from 'react';
import { useSelector } from 'react-redux';
// --- UI Components
import DataGrid from '@dtci/spellbook/DataGrid';
// --- Styles
import styles from '../../../../components/AGGrid/details-table.module.scss';
// --- Hooks
import { useAgGridApi } from '../../../../components/AGGrid/hooks';
import { useTagsDialog } from '../../../../components/dialogs/dialogs/tags';
import { useLoading } from '../../../../utils/hooks/useLoading';
import { useTagsActions } from '../../../../store/tags/useTags';
// --- Utilities
import { updateObjectValue } from '../../../../utils/updateData';
import { filterCriteria } from '../../../../utils/filterCriteria';
// --- API
import { datastageApi } from '../../../../api';
import {useTagsGridUtils} from "./utils/gridUtils";

/**
 * TagsGrid Component
 * Renders a data grid for displaying and managing tags.
 *
 * @returns {JSX.Element} The TagsGrid component.
 */
export const TagsGrid = () => {
    const agGridApi = useAgGridApi();
    const { openTagsDialog } = useTagsDialog();
    const { setLoadingTrue, setLoadingFalse } = useLoading();
    const { updateTags } = useTagsActions();
    const { tags, filterForm, tagRange, defaultFilterRange, tagSort } = useSelector((state) => state.tags);

    const {
        setSort,
        columns
    } = useTagsGridUtils({
        datastageApi,
        filterForm,
        defaultFilterRange,
        updateTags,
        handlers: {
            setLoadingTrue,
            setLoadingFalse,
            columnHandlers: []
        }
    });


    // Open dialog for editing the selected tag
    const onRowClicked = useCallback(
        (event) => {

            openTagsDialog(event.data, 'edit', async (tag) => {
                try {
                    setLoadingTrue();

                    // Update the tag object with the new tag name
                    const updatedTag = updateObjectValue(event.data, 'tagName', tag?.tagName);

                    // Send the updated tag to the API
                    await datastageApi.updateTag(updatedTag);

                    // Fetch updated tags data
                    const data = await datastageApi.tags(
                        filterCriteria(filterForm, '', 'tags'),
                        tagRange,
                        tagSort,
                    );

                    // Update store with the new tags data
                    updateTags({
                        tags: data?.content,
                        totalItems: data?.totalElements,
                        totalPages: data?.totalPages,
                        currentPage: data?.pageable?.pageNumber,
                    });
                } catch (error) {
                    console.error('Error updating tag:', error);
                } finally {
                    setLoadingFalse();
                }
            });
        },
        [
            filterForm,
            tagRange,
            tagSort,
            openTagsDialog,
            setLoadingTrue,
            setLoadingFalse,
            updateTags,
        ],
    );

    return (
        <>
            <DataGrid
                className={styles.dataGrid}
                autoHeight
                autoSizing
                agGrid={{
                    ref: agGridApi.AgGridApiRef,
                    rowData: tags,
                    headerHeight: 45,
                    onRowClicked: onRowClicked,
                }}
                bordered="none"
                columnDefs={columns}
                sortable={false}
                striped
            />
        </>
    );
};
